import type { LocaleMetaDto } from './api.js'
import { normalizeLocale, OFFICIAL_LANGUAGES, LOCALE_NATIVE_NAMES } from '@self-restaurant/shared'

/**
 * Opções do seletor "Idioma do cardápio" na revisão do import (#299/#304).
 *
 * A fonte é o catálogo de locales ativos (GET /admin/i18n/locales — tabela
 * `locales`; #419: servido já em códigos base). Códigos regionais que o
 * OCR/leitor de link detecta ('pt-PT', 'ar-SA'…) são NORMALIZADOS para a
 * base antes de entrar nas opções — o commit recebe sempre o código sem
 * região.
 */
export type LocaleOption = { code: string; label: string }

/** Nome nativo do idioma: CATÁLOGO primeiro (autónimos com maiúscula certa),
 *  Intl só para línguas fora das 14 oficiais (#428). */
export function nativeLocaleName(code: string, active: LocaleMetaDto[]): string {
  const base = normalizeLocale(code)
  const viaCatalog = active.find(
    (l) => normalizeLocale(l.code) === base || normalizeLocale(l.base) === base,
  )
  if (viaCatalog) return viaCatalog.native
  // fora das 14 línguas: Intl (minúsculas, mas é a melhor fonte disponível)
  if (!OFFICIAL_LANGUAGES.includes(base as (typeof OFFICIAL_LANGUAGES)[number])) {
    try {
      const name = new Intl.DisplayNames([code], { type: 'language' }).of(code)
      if (name && name !== code) return name
    } catch {
      /* código inválido para Intl — cai na fonte única/código */
    }
  }
  return (
    active.find((l) => normalizeLocale(l.base) === base)?.native ??
    LOCALE_NATIVE_NAMES[base as keyof typeof LOCALE_NATIVE_NAMES] ??
    code
  )
}

/**
 * Nome legível COM o código — «Português (pt)» — mesmo formato dos rótulos
 * do seletor de idioma do import (#403: também no indicador do editor de
 * produto e no aviso de tradução ausente). Código no catálogo → nome nativo
 * do catálogo; fora dele → `nativeLocaleName` (Intl, senão catálogo da base).
 */
export function localeDisplayName(code: string, active: LocaleMetaDto[]): string {
  const normalized = normalizeLocale(code)
  const exact = active.find((l) => normalizeLocale(l.code) === normalized)
  if (exact) return `${exact.native} (${normalized})`
  return `${nativeLocaleName(normalized, active)} (${normalized})`
}

export function menuLocaleOptions(
  active: LocaleMetaDto[],
  extras: Array<string | null | undefined>,
): LocaleOption[] {
  const options: LocaleOption[] = active.map((l) => ({
    code: normalizeLocale(l.code),
    label: `${l.native} (${normalizeLocale(l.code)})`,
  }))
  const known = new Set(options.map((o) => o.code))
  for (const raw of extras) {
    if (!raw?.trim()) continue
    const code = normalizeLocale(raw)
    if (!code || known.has(code)) continue
    known.add(code)
    options.push({ code, label: localeDisplayName(code, active) })
  }
  return options
}
